// Port of webapp/src/components/viewTitle.tsx — the board title row with the
// description toggle and the board actions menu (rename, duplicate, delete,
// add/remove card properties).

import {ReactElement, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import {Board, IPropertyTemplate} from '../types/board'
import {buildBoardPath} from '../common/paths'
import {Utils, IDType} from '../common/utils'
import * as mutator from '../state/mutator'
import {Icon} from './common/icons'
import {ButtonWithMenu, Menu, MenuItem, MenuSeparator} from './common/menu'
import {ConfirmDialog} from './common/dialog'
import Editable from './common/editable'
import {addablePropertyTypes} from '../state/properties'
import {useTeams} from '../store/teams'
import {useBoards} from '../store/boards'
import {useCards} from '../store/cards'

type Props = {
    board: Board
    readOnly: boolean
}


export default function ViewTitle({board, readOnly}: Props): ReactElement {
    const navigate = useNavigate()
    const teamId = useTeams((state) => state.activeTeamId)
    const boards = useBoards((state) => state.boards)
    const cards = useCards((state) => state.cards)
    const [confirmDelete, setConfirmDelete] = useState(false)
    const [descriptionDraft, setDescriptionDraft] = useState<string | null>(null)

    const duplicateBoard = async (): Promise<void> => {
        const created = await mutator.createBoardWithViews(teamId, {
            title: `${board.title}（副本）`,
            description: board.description,
            icon: board.icon,
            cardProperties: board.cardProperties,
        })
        await Promise.all(Object.values(cards).
            filter((card) => card.boardId === board.id && card.deleteAt === 0 && !card.fields.isTemplate).
            map((card) => mutator.createCard(created, undefined, {
                title: card.title,
                icon: card.fields.icon,
                properties: card.fields.properties,
            })))
        navigate(buildBoardPath({teamId, boardId: created.id}))
    }

    const deleteBoard = async (): Promise<void> => {
        setConfirmDelete(false)
        await mutator.deleteBoard(board.id)
        const next = Object.values(boards).find((b) => !b.isTemplate && b.deleteAt === 0 && b.id !== board.id)
        navigate(next ? buildBoardPath({teamId, boardId: next.id}) : buildBoardPath({teamId}))
    }

    const addProperty = (type: IPropertyTemplate['type'], label: string): void => {
        const template: IPropertyTemplate = {
            id: Utils.createGuid(IDType.BlockID),
            name: label,
            type,
            options: type === 'select' || type === 'multiSelect' ? [{
                id: Utils.createGuid(IDType.BlockID),
                value: '选项 1',
                color: 'propColorBlue',
            }] : [],
        }
        void mutator.addCardProperty(board, template)
    }

    const commitDescription = (): void => {
        if (descriptionDraft !== null && descriptionDraft !== board.description) {
            void mutator.changeBoardDescription(board.id, descriptionDraft, true)
        }
        setDescriptionDraft(null)
    }

    return (
        <div className='ViewTitle flex min-w-0 flex-1 items-start gap-2 py-1'>
            <span className='mt-1 text-2xl leading-8' aria-hidden={true}>{board.icon || '📋'}</span>

            <div className='min-w-0 flex-1'>
                <Editable
                    className='truncate text-2xl font-bold leading-8'
                    value={board.title}
                    readOnly={readOnly}
                    allowEmpty={false}
                    onChange={(title) => void mutator.changeBoardTitle(board.id, title)}
                />

                {board.showDescription && (
                    <textarea
                        className='mt-1 w-full resize-none rounded bg-main-fg/5 px-2 py-1 text-sm leading-5'
                        rows={2}
                        readOnly={readOnly}
                        placeholder='添加描述'
                        value={descriptionDraft ?? board.description}
                        onChange={(event) => setDescriptionDraft(event.target.value)}
                        onBlur={commitDescription}
                        onKeyDown={(event) => {
                            if (event.key === 'Escape') {
                                setDescriptionDraft(null)
                            }
                        }}
                    />
                )}
            </div>

            {!readOnly && (
                <ButtonWithMenu
                    className='mt-1 shrink-0'
                    button={
                        <span className='IconButton' aria-label='看板操作'>
                            <Icon name='dots-vertical' size={18}/>
                        </span>
                    }
                >
                    <Menu ariaLabel='看板操作'>
                        <MenuItem
                            icon='eye'
                            onClick={() => void mutator.changeBoardDescription(board.id, board.description, !board.showDescription)}
                        >
                            {board.showDescription ? '隐藏描述' : '显示描述'}
                        </MenuItem>
                        <MenuItem icon='duplicate' onClick={() => void duplicateBoard()}>复制看板</MenuItem>
                        <MenuSeparator/>
                        <div className='px-3 py-1 text-xxs uppercase opacity-60'>添加属性</div>
                        {addablePropertyTypes.map((item) => (
                            <MenuItem key={item.type} onClick={() => addProperty(item.type, item.label)}>
                                {item.label}
                            </MenuItem>
                        ))}
                        {board.cardProperties.length > 0 && <MenuSeparator/>}
                        {board.cardProperties.map((template) => (
                            <MenuItem
                                key={template.id}
                                icon='trash'
                                danger={true}
                                onClick={() => void mutator.deleteCardProperty(board, template)}
                            >
                                删除「{template.name}」
                            </MenuItem>
                        ))}
                        <MenuSeparator/>
                        <MenuItem icon='trash' danger={true} onClick={() => setConfirmDelete(true)}>删除看板</MenuItem>
                    </Menu>
                </ButtonWithMenu>
            )}

            {confirmDelete && (
                <ConfirmDialog
                    title={`删除「${board.title}」？`}
                    message='该看板及其卡片会被归档。mock 数据库不会真正删除任何数据，点「重置演示数据」即可恢复。'
                    confirmText='删除看板'
                    onConfirm={() => void deleteBoard()}
                    onCancel={() => setConfirmDelete(false)}
                />
            )}
        </div>
    )
}
